/**
 * Content Detail Panel
 * Side panel for viewing and editing content metadata
 */

import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import {
  getContentItem,
  updateContentItem,
  updateFormattedText,
  incrementDownloadCount,
  getWebpageChatDetails,
} from "../../services/content.service";
import { getChatConfig } from "../../services/chat.service";
import { QUERY_KEYS } from "../../utils/constants";
import { useToast } from "../ui/Toast";
import Button from "../common/Button";
import LoadingSpinner from "../common/LoadingSpinner";
import FlipbookViewer from "./FlipbookViewer";
import RichTextEditor from "./RichTextEditor";
import EmbedCodeGenerator from "./EmbedCodeGenerator";
import SuggestedQuestionsManager from "./SuggestedQuestionsManager";
import type {
  ContentItem,
  FlipbookDetails,
  ImageDetails,
  VideoLinkDetails,
  TextBlockDetails,
  FileDetails,
  WebpageChatDetails,
} from "../../types/content.types";
import { ContentType, ProcessingStatus } from "../../types/content.types";
import { formatDate } from "../../utils/formatters";
import {
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  LinkIcon,
  ClipboardIcon,
  CheckIcon,
  EyeIcon,
  EyeSlashIcon,
  PlayIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";

interface ContentDetailPanelProps {
  item: ContentItem;
  onClose: () => void;
  onUpdate: () => void;
  onDelete: () => void;
}

export default function ContentDetailPanel({
  item,
  onClose,
  onUpdate,
  onDelete,
}: ContentDetailPanelProps) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description || "");
  const [tags, setTags] = useState<string[]>(item.tags || []);
  const [tagInput, setTagInput] = useState("");
  const [isPublic, setIsPublic] = useState(item.is_public);
  const [isFeatured, setIsFeatured] = useState(item.is_featured || false);

  // Formatted text editing state
  const [editTextContent, setEditTextContent] = useState("");

  // Viewer state
  const [showViewer, setShowViewer] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch full content details
  const { data: fullItem, isLoading } = useQuery({
    queryKey: [QUERY_KEYS.CONTENT_ITEM, item.id],
    queryFn: () => getContentItem(item.id),
    placeholderData: item,
    staleTime: 0, // Always refetch to get latest details
  });

  // Fetch chat config for flipbooks
  const { data: chatConfig } = useQuery({
    queryKey: ["chatConfig", item.id],
    queryFn: () => getChatConfig(item.id),
    enabled: item.content_type === ContentType.FLIPBOOK,
  });

  // Fetch webpage chat details
  const { data: webpageChatDetails } = useQuery({
    queryKey: ["webpageChatDetails", item.id],
    queryFn: () => getWebpageChatDetails(item.id),
    enabled: item.content_type === ContentType.WEBPAGE_CHAT,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      return updateContentItem(
        item.id,
        {
          title,
          description,
          tags,
          is_public: isPublic,
          is_featured: isFeatured,
        },
        user.id,
      );
    },
    onSuccess: () => {
      showToast("success", "Content updated successfully");
      setIsEditing(false);
      onUpdate();
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CONTENT_ITEM, item.id],
      });
    },
    onError: (error: Error) => {
      showToast("error", `Failed to update: ${error.message}`);
    },
  });

  // Update text content mutation
  const updateTextMutation = useMutation({
    mutationFn: async () => {
      return updateFormattedText(item.id, editTextContent);
    },
    onSuccess: () => {
      showToast("success", "Text content updated");
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CONTENT_ITEM, item.id],
      });
    },
    onError: (error: Error) => {
      showToast("error", `Failed to update text: ${error.message}`);
    },
  });

  // Reset form when item changes
  useEffect(() => {
    setTitle(item.title);
    setDescription(item.description || "");
    setTags(item.tags || []);
    setIsPublic(item.is_public);
    setIsFeatured(item.is_featured || false);
    setIsEditing(false);
    setEditTextContent("");
  }, [item.id]);

  // Initialize text content when fullItem loads
  useEffect(() => {
    if (fullItem?.content_type === ContentType.TEXT_BLOCK && fullItem.details) {
      setEditTextContent((fullItem.details as TextBlockDetails).content || "");
    }
  }, [fullItem?.id, fullItem?.details]);

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleSave = () => {
    updateMutation.mutate();
    // Also save text content if editing a text block
    if (item.content_type === ContentType.TEXT_BLOCK && editTextContent) {
      updateTextMutation.mutate();
    }
  };

  const handleCancel = () => {
    setTitle(item.title);
    setDescription(item.description || "");
    setTags(item.tags || []);
    setIsPublic(item.is_public);
    setIsFeatured(item.is_featured || false);
    setIsEditing(false);
  };

  const handleCopyLink = () => {
    const publicUrl = getPublicContentUrl(fullItem);
    if (publicUrl) {
      navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showToast("success", "Link copied to clipboard");
    }
  };

  const handleDownload = async () => {
    const url = getDirectContentUrl(fullItem);
    if (!url) {
      return;
    }

    await incrementDownloadCount(item.id);
    window.open(url, "_blank");
  };

  const getPublicContentUrl = (
    item: ContentItem | null | undefined,
  ): string | null => {
    if (!item || !item.is_public) {
      return null;
    }
    // Return the public viewer URL
    return `${window.location.origin}/view/${item.id}`;
  };

  const getDirectContentUrl = (
    item: ContentItem | null | undefined,
  ): string | null => {
    if (!item?.details) {
      return null;
    }
    return (
      (item.details as FlipbookDetails | ImageDetails | FileDetails)
        .publicUrl || null
    );
  };

  const renderPreview = () => {
    if (!fullItem) {
      return null;
    }

    switch (fullItem.content_type) {
      case ContentType.IMAGE: {
        const details = fullItem.details as ImageDetails | undefined;
        return details?.publicUrl ? (
          <div className="bg-gray-100 rounded-lg overflow-hidden">
            <img
              src={details.publicUrl}
              alt={fullItem.title}
              className="w-full h-auto max-h-64 object-contain"
            />
          </div>
        ) : null;
      }

      case ContentType.FLIPBOOK: {
        const details = fullItem.details as FlipbookDetails | undefined;
        if (details?.processing_status === ProcessingStatus.PENDING) {
          return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-yellow-700">Processing flipbook...</p>
              <p className="text-sm text-yellow-600 mt-1">
                This may take a few minutes for large PDFs.
              </p>
            </div>
          );
        }
        if (details?.processing_status === ProcessingStatus.FAILED) {
          return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-red-700">Failed to process flipbook</p>
            </div>
          );
        }
        // Use page_urls length as source of truth, fallback to page_count
        const pageCount =
          details?.page_urls?.length || details?.page_count || 0;
        return (
          <div className="bg-gray-100 rounded-lg p-8 text-center">
            <p className="text-gray-600 mb-4">{pageCount} pages</p>
            <Button onClick={() => setShowViewer(true)}>
              <PlayIcon className="h-4 w-4 mr-2" />
              Open Flipbook Viewer
            </Button>
          </div>
        );
      }

      case ContentType.VIDEO_LINK: {
        const details = fullItem.details as VideoLinkDetails | undefined;
        if (!details) {
          return null;
        }

        // Render embedded video based on platform
        if (details.platform === "YOUTUBE" && details.video_id) {
          return (
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              <iframe
                src={`https://www.youtube.com/embed/${details.video_id}`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }
        if (details.platform === "VIMEO" && details.video_id) {
          return (
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              <iframe
                src={`https://player.vimeo.com/video/${details.video_id}`}
                className="w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }

        // Fallback for other platforms
        return (
          <div className="bg-gray-100 rounded-lg p-8 text-center">
            <a
              href={details.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 hover:text-blue-700"
            >
              Open Video
              <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" />
            </a>
          </div>
        );
      }

      case ContentType.FILE: {
        const details = fullItem.details as FileDetails | undefined;
        return (
          <div className="bg-gray-100 rounded-lg p-8 text-center">
            <p className="text-gray-600 mb-2">{details?.file_name}</p>
            {details?.file_size && (
              <p className="text-sm text-gray-500 mb-4">
                {formatFileSize(details.file_size)}
              </p>
            )}
            <Button onClick={handleDownload}>
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        );
      }

      case ContentType.TEXT_BLOCK: {
        const details = fullItem.details as TextBlockDetails | undefined;
        if (isEditing) {
          return (
            <RichTextEditor
              content={editTextContent}
              onChange={setEditTextContent}
              editable={true}
              placeholder="Edit your formatted text..."
            />
          );
        }
        return details?.content ? (
          <div
            className="prose prose-sm max-w-none bg-white rounded-lg border border-gray-200 p-4 overflow-auto max-h-96"
            dangerouslySetInnerHTML={{ __html: details.content }}
          />
        ) : (
          <div className="bg-gray-100 rounded-lg p-8 text-center">
            <p className="text-gray-500">No content yet</p>
          </div>
        );
      }

      case ContentType.WEBPAGE_CHAT: {
        const details = fullItem.details as WebpageChatDetails | undefined;
        return (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-blue-100 mx-auto mb-3 flex items-center justify-center">
              <LinkIcon className="h-6 w-6 text-blue-600" />
            </div>
            <p className="text-gray-800 font-medium mb-1">Webpage Chat</p>
            {details?.source_url && (
              <a
                href={details.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline break-all"
              >
                {details.source_url}
              </a>
            )}
            {details?.crawl_status && (
              <p className="mt-2">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    details.crawl_status === "ready"
                      ? "bg-green-100 text-green-700"
                      : details.crawl_status === "failed"
                        ? "bg-red-100 text-red-700"
                        : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {details.crawl_status === "ready"
                    ? "Ready"
                    : details.crawl_status === "pending"
                      ? "Pending"
                      : details.crawl_status === "crawling"
                        ? "Crawling..."
                        : "Failed"}
                </span>
              </p>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <>
      <div className="w-96 border-l border-gray-200 bg-white flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">Content Details</h2>
          <div className="flex items-center gap-2">
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                title="Edit"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {/* Preview */}
              {renderPreview()}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Title
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{fullItem?.title}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Description
                </label>
                {isEditing ? (
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                ) : (
                  <p className="text-gray-900">
                    {fullItem?.description || "—"}
                  </p>
                )}
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Tags
                </label>
                {isEditing ? (
                  <>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="text-gray-500 hover:text-red-500"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === ",") {
                            e.preventDefault();
                            handleAddTag();
                          }
                        }}
                        placeholder="Add tag"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={handleAddTag}
                      >
                        Add
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {fullItem?.tags && fullItem.tags.length > 0 ? (
                      fullItem.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                        >
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400">No tags</span>
                    )}
                  </div>
                )}
              </div>

              {/* Visibility */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Visibility
                </label>
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="editIsPublic"
                      checked={isPublic}
                      onChange={(e) => setIsPublic(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label
                      htmlFor="editIsPublic"
                      className="text-sm text-gray-700"
                    >
                      Public (accessible by 3rd party apps)
                    </label>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {fullItem?.is_public ? (
                      <>
                        <EyeIcon className="h-4 w-4 text-green-600" />
                        <span className="text-green-600">Public</span>
                      </>
                    ) : (
                      <>
                        <EyeSlashIcon className="h-4 w-4 text-gray-500" />
                        <span className="text-gray-500">Private</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Featured */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Featured
                </label>
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="editIsFeatured"
                      checked={isFeatured}
                      onChange={(e) => setIsFeatured(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                    />
                    <label
                      htmlFor="editIsFeatured"
                      className="text-sm text-gray-700"
                    >
                      Featured (highlighted in listings)
                    </label>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {fullItem?.is_featured ? (
                      <>
                        <svg
                          className="h-4 w-4 text-yellow-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="text-yellow-600">Featured</span>
                      </>
                    ) : (
                      <span className="text-gray-400">Not featured</span>
                    )}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Views
                  </label>
                  <p className="text-gray-900">{fullItem?.view_count || 0}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Downloads
                  </label>
                  <p className="text-gray-900">
                    {fullItem?.download_count || 0}
                  </p>
                </div>
              </div>

              {/* Meta */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="block text-gray-500">Created</label>
                  <p className="text-gray-700">
                    {fullItem?.created_at
                      ? formatDate(fullItem.created_at)
                      : "—"}
                  </p>
                </div>
                <div>
                  <label className="block text-gray-500">Created By</label>
                  <p className="text-gray-700">
                    {fullItem?.creator?.full_name ||
                      fullItem?.creator?.email?.split("@")[0] ||
                      "—"}
                  </p>
                </div>
              </div>

              {/* Public Link */}
              {fullItem?.is_public && getPublicContentUrl(fullItem) && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Public Link
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={getPublicContentUrl(fullItem) || ""}
                      className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-600"
                    />
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={handleCopyLink}
                    >
                      {copied ? (
                        <CheckIcon className="h-4 w-4" />
                      ) : (
                        <ClipboardIcon className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Embed Code Generator - for public flipbooks and webpage chats */}
              {fullItem?.is_public &&
                (fullItem.content_type === ContentType.FLIPBOOK ||
                  fullItem.content_type === ContentType.WEBPAGE_CHAT) && (
                  <div className="border-t border-gray-200 pt-6">
                    <EmbedCodeGenerator
                      contentId={fullItem.id}
                      type={
                        fullItem.content_type === ContentType.FLIPBOOK
                          ? "flipbook"
                          : "webpage"
                      }
                      allowedDomains={
                        fullItem.content_type === ContentType.WEBPAGE_CHAT
                          ? (webpageChatDetails?.allowed_domains as string[]) ||
                            []
                          : []
                      }
                    />
                  </div>
                )}

              {/* Suggested Questions Manager - for flipbooks with chat enabled */}
              {fullItem?.content_type === ContentType.FLIPBOOK &&
                chatConfig?.chat_enabled && (
                  <div className="border-t border-gray-200 pt-6">
                    <SuggestedQuestionsManager contentItemId={fullItem.id} />
                  </div>
                )}

              {/* Webpage Chat Details */}
              {fullItem?.content_type === ContentType.WEBPAGE_CHAT &&
                webpageChatDetails && (
                  <div className="border-t border-gray-200 pt-6">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">
                          Source URL
                        </label>
                        <a
                          href={webpageChatDetails.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline break-all"
                        >
                          {webpageChatDetails.source_url}
                        </a>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">
                          Crawl Status
                        </label>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            webpageChatDetails.crawl_status === "ready"
                              ? "bg-green-100 text-green-700"
                              : webpageChatDetails.crawl_status === "failed"
                                ? "bg-red-100 text-red-700"
                                : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {webpageChatDetails.crawl_status}
                        </span>
                      </div>
                      {/* Suggested Questions for Webpage Chat */}
                      <SuggestedQuestionsManager contentItemId={fullItem.id} />
                    </div>
                  </div>
                )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          {isEditing ? (
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                isLoading={
                  updateMutation.isPending || updateTextMutation.isPending
                }
              >
                Save Changes
              </Button>
            </div>
          ) : (
            <div className="flex justify-between">
              <Button variant="danger" onClick={onDelete}>
                <TrashIcon className="h-4 w-4 mr-2" />
                Delete
              </Button>
              <div className="flex gap-2">
                {fullItem?.is_public && (
                  <Button variant="secondary" onClick={handleCopyLink}>
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Copy Link
                  </Button>
                )}
                {(fullItem?.content_type === ContentType.FLIPBOOK ||
                  fullItem?.content_type === ContentType.FILE) && (
                  <Button variant="secondary" onClick={handleDownload}>
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Flipbook Viewer Modal */}
      {showViewer && fullItem?.content_type === ContentType.FLIPBOOK && (
        <FlipbookViewer
          contentItemId={fullItem.id}
          title={fullItem.title}
          onClose={() => setShowViewer(false)}
        />
      )}
    </>
  );
}
